import { tns } from 'tiny-slider/src/tiny-slider';
import Events from '@utilities/events';
import RafThrottle from '@utilities/raf-throttle';

const JS_HOOK_WRAPPER = '[js-hook-carousel-wrapper]';

const DATA_SLIDES_MOBILE = 'data-slides-mobile';
const DATA_SLIDES_TABLET_PORTRAIT = 'data-slides-tablet-portrait';
const DATA_SLIDES_TABLET_LANDSCAPE = 'data-slides-tablet-landscape';
const DATA_SLIDES_DESKTOP = 'data-slides-desktop';

const DATA_GUTTER = 'data-gutter';
const DATA_GUTTER_MOBILE = 'data-gutter-mobile';
const DATA_GUTTER_TABLET_PORTRAIT = 'data-gutter-tablet-portrait';
const DATA_GUTTER_TABLET_LANDSCAPE = 'data-gutter-tablet-landscape';
const DATA_GUTTER_DESKTOP = 'data-gutter-desktop';

const DATA_DOTS = 'data-dots';
const DATA_DOTS_CLASS = 'data-dots-class';
const DATA_CONTROLS = 'data-controls';
const DATA_NAV = 'data-nav';
const DATA_CONTROLS_CLASS = 'data-controls-class';
const DATA_NAVIGATION_CLASS = 'data-navigation-class';

const DATA_MODE = 'data-mode';
const DATA_AUTOPLAY = 'data-autoplay';
const DATA_AUTOPLAY_TIMEOUT = 'data-autoplay-timeout';
const DATA_LOOP = 'data-loop';
const DATA_SPEED = 'data-speed';
const DATA_SLIDE_BY = 'data-slideby';

const CLASS_CAROUSEL = 'c-carousel';
const CLASS_CAROUSEL_NAVIGATION = 'carousel__navigation';
const CLASS_CAROUSEL_DOTS = 'carousel__dots';
const CLASS_CAROUSEL_HAS_DOTS = 'carousel--has-dots';
const CLASS_CAROUSEL_CONTROLS = 'carousel__controls';
const CLASS_CAROUSEL_HAS_CONTROLS = 'carousel--has-controls';

const SVG_PREV =
    '<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 8 14"><path d="M7.7,1.7c0.4-0.4,0.4-1,0-1.4c-0.4-0.4-1-0.4-1.4,0l-6,6c-0.4,0.4-0.4,1,0,1.4l6,6c0.4,0.4,1,0.4,1.4,0c0.4-0.4,0.4-1,0-1.4 L2.4,7L7.7,1.7z"/></svg>';
const SVG_NEXT =
    '<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 8 14"><path d="M0.3,12.3c-0.4,0.4-0.4,1,0,1.4c0.4,0.4,1,0.4,1.4,0l6-6c0.4-0.4,0.4-1,0-1.4l-6-6c-0.4-0.4-1-0.4-1.4,0s-0.4,1,0,1.4L5.6,7L0.3,12.3z"/></svg>';

const defaultSpeed = 700;
const defaultTimeout = 5000;

const breakpoints = {
    mobilePlus: '480',
    tabletPortrait: '768',
    tabletLandscape: '1024',
    desktop: '1280',
};

class Carousel {
    constructor(element) {
        this.element = element;
        this.slides = this.element.children;

        this.slidesMobile = parseFloat(this.element.getAttribute(DATA_SLIDES_MOBILE)) || 1;
        this.slidesTabletPortrait = parseFloat(this.element.getAttribute(DATA_SLIDES_TABLET_PORTRAIT)) || this.slidesMobile;
        this.slidesTabletLandscape = parseFloat(this.element.getAttribute(DATA_SLIDES_TABLET_LANDSCAPE)) || this.slidesTabletPortrait;
        this.slidesDesktop = parseFloat(this.element.getAttribute(DATA_SLIDES_DESKTOP)) || this.slidesTabletLandscape;

        this.gutter = parseInt(this.element.getAttribute(DATA_GUTTER)) >= 0 ? parseInt(this.element.getAttribute(DATA_GUTTER)) : 20;
        this.gutterMobile = parseInt(this.element.getAttribute(DATA_GUTTER_MOBILE)) || this.gutter;
        this.gutterTabletPortrait = parseInt(this.element.getAttribute(DATA_GUTTER_TABLET_PORTRAIT)) || this.gutterMobile;
        this.gutterTabletLandscape = parseInt(this.element.getAttribute(DATA_GUTTER_TABLET_LANDSCAPE)) || this.gutterTabletPortrait;
        this.gutterDesktop = parseInt(this.element.getAttribute(DATA_GUTTER_DESKTOP)) || this.gutterTabletLandscape;

        this.nav = this.element.getAttribute(DATA_NAV) === 'false' ? false : true;
        this.navigationClass = this.element.getAttribute(DATA_NAVIGATION_CLASS) || CLASS_CAROUSEL_NAVIGATION;

        this.dots = this.element.getAttribute(DATA_DOTS) === 'true' ? true : false;
        this.dotsClass = this.element.getAttribute(DATA_DOTS_CLASS) || CLASS_CAROUSEL_DOTS;

        this.controls = this.element.getAttribute(DATA_CONTROLS) === 'true' ? true : false;
        this.controlsClass = this.element.getAttribute(DATA_CONTROLS_CLASS) || CLASS_CAROUSEL_CONTROLS;

        this.slideby = parseInt(this.element.getAttribute(DATA_SLIDE_BY)) || 1;
        this.autoplay = this.element.getAttribute(DATA_AUTOPLAY) === 'true' ? true : false;
        this.autoplayTimeout = parseInt(this.element.getAttribute(DATA_AUTOPLAY_TIMEOUT)) || defaultTimeout;
        this.loop = this.element.getAttribute(DATA_LOOP) === 'true' ? true : false;
        this.speed = parseInt(this.element.getAttribute(DATA_SPEED)) || defaultSpeed;
        this.mode = this.element.getAttribute(DATA_MODE) || 'carousel';

        this.prepareCarousel();

        if (this.controls) {
            this.createControls();
        }

        if (this.dots) {
            this.createDots();
        }

        this.initSlider();

        this.bindEvents();
    }

    bindEvents() {
        RafThrottle.set([
            {
                element: window,
                event: 'resize',
                namespace: 'eventNamespace',
                fn: () => this.refreshSlider(),
            },
        ]);
    }

    initSlider() {
        this.instance = tns({
            container: this.element,
            items: this.slidesMobile,
            gutter: this.gutter,
            responsive: {
                [breakpoints.tabletPortrait]: {
                    items: this.slidesTabletPortrait,
                    gutter: this.gutterTabletPortrait,
                },
                [breakpoints.tabletLandscape]: {
                    items: this.slidesTabletLandscape,
                    gutter: this.gutterTabletLandscape,
                },
                [breakpoints.desktop]: {
                    items: this.slidesDesktop,
                    gutter: this.gutterDesktop,
                },
            },
            mouseDrag: true,
            speed: this.speed,
            slideBy: this.slideby,
            autoplay: this.autoplay,
            autoplayTimeout: this.autoplayTimeout,
            mode: this.mode,
            controls: this.controls,
            prevButton: this.btnPrev || false,
            nextButton: this.btnNext || false,
            loop: this.loop,
            nav: this.nav,
            autoplayButtonOutput: false,
            navContainer: this.navContainer || false,
            preventScrollOnTouch: 'auto',
            onInit: () => this.onInit(),
        });
    }

    onInit() {
        Events.$trigger('lazyimage::update');

        const carouselWrapper = this.element.closest(JS_HOOK_WRAPPER);

        if (carouselWrapper) {
            carouselWrapper.classList.add('carousel--is-loaded');
        }
    }

    refreshSlider() {
        this.instance.destroy();
        this.instance = this.instance.rebuild();
    }

    prepareCarousel() {
        const carouselWrapper = document.createElement('div');
        carouselWrapper.classList.add(CLASS_CAROUSEL);
        this.element.parentNode.insertBefore(carouselWrapper, this.element);
        carouselWrapper.appendChild(this.element);

        Array.from(this.slides).forEach(slide => {
            const slideWrapper = document.createElement('div');
            slide.parentNode.insertBefore(slideWrapper, slide);
            slideWrapper.appendChild(slide);
        });
    }

    createNavigation() {
        this.navigation = document.createElement('div');
        this.navigation.classList.add(this.navigationClass);
        this.element.parentNode.insertBefore(this.navigation, this.element.nextSibling);
    }

    createControls() {
        const controlsWrapper = document.createElement('div');
        controlsWrapper.classList.add(this.controlsClass);
        this.element.closest('.' + CLASS_CAROUSEL).classList.add(CLASS_CAROUSEL_HAS_CONTROLS);

        if (!this.navigation) {
            this.createNavigation();
        }

        this.navigation.appendChild(controlsWrapper);

        this.btnPrev = document.createElement('button');
        this.btnPrev.classList.add(`${this.controlsClass}-button`);
        this.btnPrev.innerHTML = SVG_PREV;
        controlsWrapper.appendChild(this.btnPrev);

        this.btnNext = document.createElement('button');
        this.btnNext.classList.add(`${this.controlsClass}-button`);
        this.btnNext.innerHTML = SVG_NEXT;
        controlsWrapper.appendChild(this.btnNext);
    }

    createDots() {
        this.navContainer = document.createElement('div');
        this.navContainer.classList.add(this.dotsClass);
        this.element.closest('.' + CLASS_CAROUSEL).classList.add(CLASS_CAROUSEL_HAS_DOTS);

        if (!this.navigation) {
            this.createNavigation();
        }

        this.navigation.appendChild(this.navContainer);

        Array.from(this.slides).forEach(e => {
            const btn = document.createElement('button');
            btn.classList.add(`${this.dotsClass}-button`);

            const dotIconHtml = document.createElement('span');
            dotIconHtml.classList.add(`${this.dotsClass}-button-icon`);
            btn.appendChild(dotIconHtml);

            const dotLabel = e.firstElementChild.getAttribute('data-dot-label');

            if (dotLabel) {
                const dotLabelHtml = document.createElement('span');
                dotLabelHtml.classList.add(`${this.dotsClass}-button-label`);
                dotLabelHtml.innerHTML = dotLabel;
                btn.appendChild(dotLabelHtml);
            }

            this.navContainer.appendChild(btn);
        });
    }
}

export default Carousel;
